import type { DiasSemana, TipoPromocion } from './types';
import { ventanaEnPalabras } from './ventanaDisponibilidad';

/*
  Sprint 74 — Se re-exporta en lugar de tener una segunda copia.

  `diasEnPalabras` vivía también aquí. Dos redacciones del mismo dato divergen tarde o temprano, y
  entonces la promoción diría una cosa en el formulario y otra en la tarjeta de la lista.
*/
export { diasEnPalabras } from './ventanaDisponibilidad';

/**
 * Sprint 74 — Lo que la promoción va a hacer, dicho en español.
 *
 * ## Por qué existe
 *
 * El formulario de promociones tenía quince controles y ninguna forma de saber qué se estaba
 * creando. La dueña elegía "Tipo: % sobre subtotal elegible", ponía números en "Cantidad mínima",
 * "Prioridad" y "Monto mínimo del pedido", guardaba… y **se enteraba de lo que había hecho cuando
 * llegaba un cliente**. Una promoción que no se puede leer antes de guardarla es una promoción que
 * se prueba con el dinero del cliente.
 *
 * Dictamen de MASTER CHEF POS: *"las promociones se capturan por jugada con nombre, no por
 * parámetros"*, y una de sus leyes de veto es **no mostrar un número que el sistema no pueda
 * explicar de dónde salió**. Esto es la mitad barata de ese dictamen: no cambia el modelo ni inventa
 * cómo opera el negocio, y convierte quince campos sueltos en una frase que se lee y se aprueba.
 *
 * ## Por qué es un módulo aparte y puro
 *
 * Porque hay que poder probarlo con casos, y porque la misma frase sirve para el formulario (antes
 * de guardar) y para la tarjeta de la lista (después). Dos redacciones distintas de la misma promo
 * serían dos verdades distintas.
 */

export interface PromoEnPalabras {
  /** Qué va a pasar, en una frase. Vacío si falta información para poder afirmarlo. */
  frase: string;
  /** Qué falta para que la promoción tenga sentido, en lenguaje de negocio. */
  falta: string[];
  /** Advertencia de dinero, si la combinación puede regalar producto. */
  advertencia: string | null;
}

export interface EntradaPromoEnPalabras {
  tipo: TipoPromocion;
  /** Por ciento (1–100) para el descuento porcentual; centavos para los demás. */
  valor: number;
  cantidadMinima: number;
  /** Centavos. 0 = sin mínimo de pedido. */
  montoMinimoPedido: number;
  /** Centavos. null o 0 = sin tope. */
  topeMonto: number | null;
  productoRegaloNombre: string | null;
  cantidadRegalo: number;
  regaloDescuentaDelTotal: boolean;
  /** Nombres de los productos y paquetes elegibles que están marcados. */
  nombresElegibles: string[];
  /** Cuántos hay en total, para poder decir "cualquier cosa del menú". */
  totalElegibles: number;
  /** null = todos los días. */
  dias: DiasSemana | null;
  /** Sprint 74 — Minutos desde la medianoche. null = todo el día. */
  horaInicio: number | null;
  horaFin: number | null;
  /** Sprint 74 — Sólo `N_POR_PRECIO`: el "2" de "2 x $15". */
  cantidadPaquete: number | null;
  acumulable: boolean;
}

/** "$1,234.50" — con separador de miles, que es como se lee un importe. */
export function pesos(centavos: number): string {
  const signo = centavos < 0 ? '-' : '';
  const abs = Math.abs(centavos);
  const enteros = Math.floor(abs / 100).toLocaleString('es-MX');
  const dec = String(abs % 100).padStart(2, '0');
  return `${signo}$${enteros}.${dec}`;
}

/**
 * A qué aplica. Con todo el menú marcado se dice así en lugar de listar 35 nombres, porque
 * "cualquier cosa del menú" es lo que la dueña quiso decir y la lista de 35 no se lee.
 */
function alcanceEnPalabras(nombres: string[], total: number): string {
  if (nombres.length === 0) return '';
  if (total > 0 && nombres.length === total) return 'cualquier cosa del menú';
  if (nombres.length <= 3) {
    if (nombres.length === 1) return nombres[0];
    return `${nombres.slice(0, -1).join(', ')} o ${nombres[nombres.length - 1]}`;
  }
  return `${nombres.length} productos del menú`;
}

export function promoEnPalabras(e: EntradaPromoEnPalabras): PromoEnPalabras {
  const falta: string[] = [];
  if (e.nombresElegibles.length === 0) falta.push('Falta marcar a qué productos o paquetes aplica.');
  if (e.tipo === 'REGALO') {
    if (!e.productoRegaloNombre) falta.push('Falta elegir qué se regala.');
  } else if (e.valor <= 0) {
    falta.push(
      e.tipo === 'DESCUENTO_PORCENTAJE'
        ? 'Falta cuánto por ciento se descuenta.'
        : e.tipo === 'PRECIO_FIJO'
          ? 'Falta el precio especial.'
          : e.tipo === 'N_POR_PRECIO'
            ? 'Falta el precio de la promoción (el "$15" de "2 x $15").'
            : 'Falta cuánto dinero se descuenta.',
    );
  }
  if (e.tipo === 'N_POR_PRECIO' && (e.cantidadPaquete ?? 0) < 2) {
    falta.push('Falta cuántas piezas entran en el precio (el "2" de "2 x $15").');
  }
  // Sin la tabla local de días, se pregunta directo: ningún día marcado = no aplica nunca.
  if (e.dias && Object.values(e.dias).every((marcado) => !marcado)) {
    falta.push('No quedó marcado ningún día de la semana.');
  }

  /*
    DT-070 — Dos promociones acumulables sobre el mismo producto pueden llevar el precio a cero. La
    casilla "Acumulable con otras" no decía nada de eso: es una casilla que puede regalar el
    inventario. Aquí se nombra la consecuencia donde se toma la decisión.
  */
  const advertencia = e.acumulable
    ? 'Esta promoción se junta con las demás que también estén marcadas para juntarse. Si se encima más de una sobre el mismo producto, entre todas pueden dejarlo casi en cero.'
    : null;

  if (falta.length > 0) return { frase: '', falta, advertencia };

  const alcance = alcanceEnPalabras(e.nombresElegibles, e.totalElegibles);
  const cuando: string[] = [];
  /*
    Sprint 74 — El horario va con los días y al principio de la frase, que es como se anuncia una
    promoción: "los miércoles, jueves y viernes de 1:00 p.m. a 5:00 p.m., …". Es también el dato que
    la dueña necesita ver confirmado, porque es el que se le regalaba: sin horas, la promo de $599
    aplicaba a las nueve de la noche.
  */
  const ventana = ventanaEnPalabras({
    diasSemana: e.dias,
    horaInicio: e.horaInicio,
    horaFin: e.horaFin,
  });
  if (ventana) cuando.push(ventana);
  cuando.push(
    e.cantidadMinima > 1
      ? `cuando el cliente pida ${e.cantidadMinima} o más de ${alcance}`
      : `cuando el pedido incluya ${alcance}`,
  );
  if (e.montoMinimoPedido > 0) cuando.push(`y el pedido pase de ${pesos(e.montoMinimoPedido)}`);

  let efecto: string;
  switch (e.tipo) {
    case 'DESCUENTO_PORCENTAJE':
      efecto = `se le descuenta ${e.valor}% de esos productos`;
      if (e.topeMonto && e.topeMonto > 0) efecto += `, sin pasar de ${pesos(e.topeMonto)}`;
      break;
    case 'DESCUENTO_MONTO':
      efecto = `se le descuentan ${pesos(e.valor)}`;
      break;
    case 'PRECIO_FIJO':
      efecto = `cada uno se le cobra a ${pesos(e.valor)} en lugar de su precio normal`;
      break;
    case 'N_POR_PRECIO':
      efecto = `cada ${e.cantidadPaquete} se le cobran ${pesos(e.valor)} en total, y lo que sobre a precio normal`;
      break;
    case 'REGALO':
      efecto = e.regaloDescuentaDelTotal
        ? `se le descuenta del total el precio de ${e.cantidadRegalo} ${e.productoRegaloNombre}`
        : `se le agrega ${e.cantidadRegalo} ${e.productoRegaloNombre} de regalo, sin costo`;
      break;
  }

  // Mayúscula inicial: es una frase que se lee sola, no un fragmento.
  const texto = `${cuando.join(' ')}, ${efecto}.`;
  return { frase: texto.charAt(0).toUpperCase() + texto.slice(1), falta: [], advertencia };
}
